import React, { useEffect, useState } from "react";
import axios from "axios";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Stack,
  Typography
} from "@mui/material";
import { useDispatch } from "react-redux";
import { getToken } from "common/services/tempService";
import { buildGdxApiUrl } from "common/utils/api.util";

interface DocumentType {
  id?: string;
  name: string;
  description?: string;
}

interface EditDocumentTypeDialogProps {
  open: boolean;
  document?: DocumentType | null;
  onClose: () => void;
}

const EditDocumentTypeDialog: React.FC<EditDocumentTypeDialogProps> = ({
  open,
  document,
  onClose
}) => {
  const dispatch = useDispatch();

  const isEditMode = Boolean(document?.id);

  const [name, setName] = useState("");
  const [description, setDescription] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [touched, setTouched] = useState(false);
  const [descTouched, setDescTouched] = useState(false);

  useEffect(() => {
    if (document) {
      setName(document.name || "");
      setDescription(document.description || "");
    } else {
      setName("");
      setDescription("");
    }
    setTouched(false); // Reset touched state when dialog opens
    setDescTouched(false);
  }, [document, open]);

  const handleSubmit = async () => {
    if (!name.trim() || !description.trim()) return;

    setSubmitting(true);

    try {
      const token = await getToken();
      if (isEditMode && document?.id) {
        // PUT: update document type
        await axios.put(
          buildGdxApiUrl(`gdx/database/document-types/${document.id}`),
          {
            name: name.trim(),
            description: description.trim()
          },
          {
            headers: {
              // Authorization: token
            }
          }
        );
      } else {
        // POST: create new document type
        await axios.post(
          buildGdxApiUrl("gdx/database/document-types"),
          {
            name: name.trim(),
            description: description.trim(),
            is_active: true,
            is_deleted: false
          },
          {
            headers: {
              // Authorization: token
            }
          }
        );
      }
      onClose();
    } catch (err) {
      // Optionally show error
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="sm">
      <DialogTitle sx={{ pb: 1 }}>
        <Typography
          variant="h6"
          sx={{ fontWeight: 600, color: "text.primary" }}
        >
          {isEditMode && document ? "Edit Document Type" : "Add Document Type"}
        </Typography>
      </DialogTitle>

      <DialogContent dividers sx={{ backgroundColor: "#FFFDE7" }}>
        <Stack spacing={2} mt={1}>
          <TextField
            label="Document Type Name"
            value={name}
            onChange={(e) => {
              setName(e.target.value);
              if (!touched) setTouched(true);
            }}
            required
            fullWidth
            sx={{ backgroundColor: "#FFF", borderRadius: 1 }}
            error={touched && !name.trim()}
            helperText={touched && !name.trim() ? "Name is required" : ""}
            onBlur={() => setTouched(true)}
            inputProps={{ maxLength: 15 }}
          />

          <TextField
            label="Document Description"
            value={description}
            onChange={(e) => {
              setDescription(e.target.value);
              if (!descTouched) setDescTouched(true);
            }}
            required
            fullWidth
            multiline
            rows={3}
            sx={{ backgroundColor: "#FFF", borderRadius: 1 }}
            inputProps={{ maxLength: 100 }}
            error={descTouched && !description.trim()}
            helperText={
              !description.trim() && descTouched
                ? "Description is required"
                : description.length > 90
                ? `${100 - description.length} characters left`
                : ""
            }
            onBlur={() => setDescTouched(true)}
          />
        </Stack>
      </DialogContent>

      <DialogActions>
        <Button
          onClick={onClose}
          disabled={submitting}
          sx={{
            backgroundColor: "var(--branding, #FFD600)",
            color: "#222",
            fontWeight: 600,
            boxShadow: 2,
            "&:hover": { backgroundColor: "#FFEA70" }
          }}
        >
          Cancel
        </Button>
        <Button
          variant="contained"
          onClick={handleSubmit}
          disabled={!name.trim() || submitting}
          sx={{
            backgroundColor: "var(--branding, #FFD600)",
            color: "#222",
            fontWeight: 600,
            boxShadow: 2,
            "&:hover": { backgroundColor: "#FFEA70" }
          }}
        >
          {isEditMode ? "Update" : "Create"}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default EditDocumentTypeDialog;
